import React, { useState, useEffect } from 'react'
import classes from './timeline.module.css'
import { titleCase } from '../../../../../Utility'
import { NegotiateDateFormatter } from '../../../../../Constants'
import { BID_STATUS } from '../../../negotiationConstants'

/**
 * Timeline component that displays a list of rate updates in a timeline format.
 *
 * @component
 * @param {Object[]} rate_list - List of rate information to be displayed in the timeline.
 * @param {number} index - Index of the timeline in the parent component.
 * @param {Object} enquiry - Information related to the enquiry.
 * @param {string} type - Type of the timeline item (e.g., 'indent' or 'enquiry').
 * @returns {React.ReactElement} The Timeline component.
 */
const Timeline = ({ rate_list = [], index, enquiry, type }) => {

    const [list, setList] = useState([])

    /** Rate List Updated for Timeline */
    useEffect(() => {
        var newlist = []
        if (rate_list && rate_list.length > 0) {
            rate_list.map((rates) => {
                let defaultDate = rates.rate_submission_date || rates.submission_date || rates.accepted_at || rates.approved_at || rates.negotiated_at || rates.rejected_at;
                if (rates.accepted_at === rates.rate_submission_date && rates.bid_status === BID_STATUS.ACCEPTED) {
                    newlist.push({
                        rate: rates.rate,
                        bid_status: rates.bid_status,
                        rate_submission_date: rates.accepted_at ? rates.accepted_at : rates.approved_at ? rates.approved_at : defaultDate,
                        rate_by: 'transporter',
                        remarks: rates?.remark,
                        poc_name: rates?.poc_name
                    });
                } else if (rates.bid_status === BID_STATUS.ACCEPTED) {
                    newlist.push({
                        rate: rates.rate,
                        bid_status: rates.bid_status,
                        rate_submission_date: rates.accepted_at ? rates.accepted_at : rates.approved_at ? rates.approved_at : defaultDate,
                        rate_by: 'company',
                        accepted_by: rates.accepted_by || rates.approved_by,
                        remarks: rates?.remark,
                        poc_name: rates?.poc_name
                    });
                } else if (rates.bid_status === BID_STATUS.APPROVED) {
                    newlist.push({
                        rate: rates.rate,
                        bid_status: rates.bid_status,
                        rate_submission_date: rates.approved_at || rates.accepted_at || defaultDate,
                        rate_by: 'company',
                        approved_by: rates.approved_by || rates.accepted_by,
                        remarks: rates?.remark,
                        poc_name: rates?.poc_name
                    });
                }
                if (rates.bid_status === BID_STATUS.REJECTED) {
                    newlist.push({
                        rate: rates.rate,
                        bid_status: rates.bid_status,
                        rate_submission_date: rates.rejected_at ? rates.rejected_at : defaultDate,
                        rate_by: 'company',
                        rejected_by: rates.rejected_by,
                        remarks: rates?.remark,
                        poc_name: rates?.poc_name
                    });
                }
                if (rates.bid_status === BID_STATUS.NEGOTIATED && rates.rate !== undefined) {
                    const isAgent = (rates.negotiated_by || '').toLowerCase().includes('lorri agent');
                    newlist.push({
                        rate: rates.negotiated_rate,
                        bid_status: rates.bid_status,
                        rate_by: isAgent ? 'agent' : 'company',
                        rate_submission_date: rates.negotiated_at || defaultDate,
                        negotiated_by: rates.negotiated_by,
                        remarks: rates?.remark,
                        poc_name: rates?.poc_name
                    });
                }
                if (rates.bid_status === BID_STATUS.PENDING) {
                    newlist.push({
                        rate: rates.rate,
                        bid_status: rates.bid_status,
                        rate_by: 'transporter',
                        rate_submission_date: defaultDate,
                        remarks: rates?.remark,
                        poc_name: rates?.poc_name
                    });
                }
            });
            setList(newlist)
        }
    }, [rate_list])

    const getStatusLabel = (bid_status) => {
        if (bid_status === BID_STATUS.NEGOTIATED) return 'Counter Offer';
        if (bid_status === BID_STATUS.PENDING) return 'Pending';
        return titleCase(bid_status);
    }

    const getPersonLabel = (rates) => {
        if (rates.bid_status === BID_STATUS.NEGOTIATED) {
            if (rates.rate_by === 'agent') return 'LoRRI Agent';
            return rates.negotiated_by || titleCase(rates.rate_by);
        }
        if (rates.bid_status === BID_STATUS.REJECTED)   return rates.rejected_by  || titleCase(rates.poc_name);
        if (rates.bid_status === BID_STATUS.ACCEPTED)   return rates.accepted_by  || titleCase(rates.poc_name);
        if (rates.bid_status === BID_STATUS.APPROVED)   return rates.approved_by  || titleCase(rates.poc_name);
        return titleCase(rates.poc_name);
    }

    return (
        <div id={`timeline${index}`} className={classes.timelineWrapper}>
            <div className={classes.timelineContainer}>

                {/* ── "Created" node ── */}
                <div className={classes.timelineItem}>
                    <div className={`${classes.dot} ${classes.dotCreated}`} />
                    <div className={`${classes.card} ${classes.cardCreated}`}>
                        <span className={classes.cardCreatedLabel}>
                            {type === 'indent' ? 'Indent Created' : 'Enquiry Created'}
                        </span>
                        <time className={classes.cardCreatedDate}>
                            {type === 'indent'
                                ? NegotiateDateFormatter(enquiry.indent_created_on)
                                : NegotiateDateFormatter(enquiry.submission_date)}
                        </time>
                    </div>
                </div>

                {/* ── Rate events ── */}
                {list && list.length > 0 &&
                    list.map((rates, idx) => {
                        const isTransporter = rates.rate_by === 'transporter';
                        const isAgent = rates.rate_by === 'agent';
                        const colorClass = isAgent ? classes.colorAgent : isTransporter ? classes.colorTransporter : classes.colorCompany;
                        const dotClass   = isAgent ? classes.dotAgent   : isTransporter ? classes.dotTransporter   : classes.dotCompany;
                        const cardClass  = isAgent ? classes.cardAgent  : isTransporter ? classes.cardTransporter  : classes.cardCompany;

                        return (
                            <div className={classes.timelineItem} key={idx}>
                                <div className={`${classes.dot} ${dotClass}`} />
                                <div className={`${classes.card} ${cardClass}`}>
                                    {isAgent &&
                                        <span className={classes.agentBadge}>
                                            <i className="fa fa-magic" /> LoRRI Agent
                                        </span>
                                    }
                                    <span className={`${classes.cardRate} ${colorClass}`}>
                                        {rates.rate ? Number(rates.rate).toLocaleString('en-IN') : rates.rate}
                                    </span>
                                    {!isAgent &&
                                        <span className={`${classes.cardName} ${colorClass}`}>
                                            {getPersonLabel(rates)}
                                        </span>
                                    }
                                    <span className={`${classes.cardStatus} ${isAgent ? classes.cardStatusAgent : colorClass}`}>
                                        {getStatusLabel(rates.bid_status)}
                                    </span>
                                    <time className={classes.cardDate}>
                                        {NegotiateDateFormatter(rates.rate_submission_date)}
                                    </time>
                                    {rates.remarks &&
                                        <div className={classes.cardRemarks}>{rates.remarks}</div>
                                    }
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        </div>
    );
}

export default Timeline